import { useEffect, useState } from "react";
import { ReactLenis } from "lenis/react";
import { ArrowLeft } from "lucide-react";
import { Footer } from "@/components/Footer";
import { LOGO_EMBLEM } from "@/lib/brand";
import { getContent } from "@/lib/api";
import { DEFAULT_SECTIONS, resolveSection } from "@/lib/contentDefaults";

const SUBHEADINGS = new Set([
  "Personal Information",
  "Ticketing Information",
  "Communications",
  "Event Information",
  "Images and Video",
  "Website Usage Information",
  "Cookies",
]);

function parseContent(content) {
  const lines = content
    .split("\n")
    .map((line) => line.trim())
    .filter(Boolean);

  const blocks = [];

  for (let index = 0; index < lines.length; index += 1) {
    const line = lines[index];

    if (/^\d+\.\s/.test(line)) {
      blocks.push({ type: "heading", text: line });
      continue;
    }

    if (SUBHEADINGS.has(line)) {
      blocks.push({ type: "subheading", text: line });
      continue;
    }

    if (line.startsWith("• ")) {
      const items = [];
      let cursor = index;
      while (cursor < lines.length && lines[cursor].startsWith("• ")) {
        items.push(lines[cursor].slice(2));
        cursor += 1;
      }
      blocks.push({ type: "list", items });
      index = cursor - 1;
      continue;
    }

    blocks.push({ type: "paragraph", text: line });
  }

  return blocks;
}

export default function LegalPage({ title, lastUpdated, content }) {
  const [footerSection, setFooterSection] = useState(DEFAULT_SECTIONS.footer);

  useEffect(() => {
    window.scrollTo({ top: 0, left: 0 });

    getContent()
      .then((data) => {
        const footer = resolveSection(data?.sections, "footer", true);
        if (footer) setFooterSection(footer);
      })
      .catch(() => {
        // Keep the default footer when CMS content is unavailable.
      });
  }, []);

  const blocks = parseContent(content);

  return (
    <ReactLenis root options={{ lerp: 0.09, smoothWheel: true }}>
      <div className="grain min-h-screen bg-[#050505] text-[#FAFAFA]">
        <header className="sticky top-0 z-50 bg-black/90 backdrop-blur-md border-b border-white/10">
          <div className="max-w-[1200px] mx-auto px-6 md:px-12 h-20 flex items-center justify-between gap-6">
            <a href="/" aria-label="Susegado Strike home" className="flex items-center group">
              <img src={LOGO_EMBLEM} alt="Susegado Strike" className="h-11 md:h-12 w-auto transition-transform duration-300 group-hover:scale-105" />
            </a>
            <a
              href="/"
              className="font-oswald uppercase tracking-[0.18em] text-xs text-[#A3A3A3] hover:text-[#E85A24] transition-colors inline-flex items-center gap-2"
            >
              <ArrowLeft size={14} /> Back to site
            </a>
          </div>
        </header>

        <main className="max-w-[1200px] mx-auto px-6 md:px-12 pt-16 md:pt-24 pb-24">
          <div className="max-w-4xl">
            <p className="font-oswald uppercase tracking-[0.3em] text-xs text-[#E85A24] mb-4">Legal</p>
            <h1 className="font-anton uppercase text-5xl md:text-7xl lg:text-8xl leading-[0.9] mb-6">
              {title}
            </h1>
            <p className="font-dm text-sm text-[#A3A3A3] mb-16">Last Updated: {lastUpdated}</p>

            <div className="space-y-5">
              {blocks.map((block, index) => {
                if (block.type === "heading") {
                  return (
                    <h2 key={`${block.type}-${index}`} className="font-anton uppercase text-2xl md:text-3xl text-[#FAFAFA] pt-10 pb-1 border-t border-white/10 first:border-t-0 first:pt-0">
                      {block.text}
                    </h2>
                  );
                }

                if (block.type === "subheading") {
                  return (
                    <h3 key={`${block.type}-${index}`} className="font-oswald uppercase tracking-[0.16em] text-sm text-[#E85A24] pt-3">
                      {block.text}
                    </h3>
                  );
                }

                if (block.type === "list") {
                  return (
                    <ul key={`${block.type}-${index}`} className="font-dm text-[#C4C4C4] leading-7 space-y-2 pl-5 list-disc marker:text-[#E85A24]">
                      {block.items.map((item) => <li key={item}>{item}</li>)}
                    </ul>
                  );
                }

                return (
                  <p key={`${block.type}-${index}`} className="font-dm text-[#C4C4C4] leading-7">
                    {block.text}
                  </p>
                );
              })}
            </div>
          </div>
        </main>

        <Footer section={footerSection} />
      </div>
    </ReactLenis>
  );
}
